import { Box, Typography, Chip } from "@mui/material";
import { CheckCircle as CheckCircleIcon, RadioButtonUnchecked as RadioButtonUncheckedIcon } from "@mui/icons-material";
import { WIZARD_STEP_TITLES, type WizardStep } from "../../WizardContainer";

// ============================================================================
// Types
// ============================================================================

export interface StepProgressProps {
	currentStep: WizardStep;
	visibleSteps: WizardStep[];
	completedSteps: Set<WizardStep>;
	resolvedSteps: Set<WizardStep>;
	visitedSteps: Set<WizardStep>;
	stepIssueCounts: Partial<Record<WizardStep, number>>;
	onStepClick?: (step: WizardStep) => void;
}

// ============================================================================
// Component
// ============================================================================

export function StepProgress({
	currentStep,
	visibleSteps,
	completedSteps,
	resolvedSteps,
	visitedSteps,
	stepIssueCounts,
	onStepClick,
}: StepProgressProps) {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 0,
				width: "100%",
			}}
		>
			{visibleSteps.map((step, index) => {
				const isActive = step === currentStep;
				const isCompleted = completedSteps.has(step);
				const isResolved = resolvedSteps.has(step);
				const issueCount = stepIssueCounts[step] ?? 0;

				// Show checkmark if step is completed OR resolved
				const showCheckmark = isCompleted || isResolved;

				// Show issue count only if step has issues AND is not showing checkmark
				const showIssueCount = step !== "summary" && issueCount > 0 && !showCheckmark;

				// Clickable if the step has been visited and is not the current step
				const isClickable = !isActive && visitedSteps.has(step) && !!onStepClick;

				return (
					<Box key={step} sx={{ display: "flex", flexDirection: "column" }}>
						{/* Vertical connector line (except for first step) */}
						{index > 0 && (
							<Box
								sx={{
									width: 2,
									height: 16,
									bgcolor: completedSteps.has(visibleSteps[index - 1]) ? "success.main" : "divider",
									ml: "25px",
								}}
							/>
						)}

						{/* Step row */}
						<Box
							data-testid={`step-${step}`}
							data-active={isActive.toString()}
							data-completed={isCompleted.toString()}
							data-resolved={isResolved.toString()}
							role={isClickable ? "button" : undefined}
							onClick={isClickable ? () => onStepClick(step) : undefined}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								px: 2,
								py: 0.5,
								cursor: isClickable ? "pointer" : "default",
								borderRadius: 1,
								"&:hover": isClickable
									? {
											bgcolor: "action.hover",
										}
									: undefined,
							}}
						>
							{/* Step icon */}
							{showCheckmark ? (
								<CheckCircleIcon data-testid="check-icon" sx={{ color: "success.main", fontSize: 20 }} />
							) : (
								<RadioButtonUncheckedIcon
									sx={{
										color: isActive ? "primary.main" : "text.disabled",
										fontSize: 20,
									}}
								/>
							)}

							{/* Step label */}
							<Typography
								variant="body2"
								sx={{
									fontWeight: isActive ? 600 : 400,
									color: isActive ? "primary.main" : isCompleted ? "success.main" : "text.secondary",
									flex: 1,
								}}
							>
								{WIZARD_STEP_TITLES[step]}
							</Typography>

							{/* Issue count badge */}
							{showIssueCount && (
								<Chip
									data-testid="issue-count-badge"
									label={issueCount}
									size="small"
									color={isActive ? "primary" : "default"}
									sx={{
										height: 20,
										fontSize: "0.75rem",
									}}
								/>
							)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}
